import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  Refresh,
  Folder,
  Schedule,
  LoginOutlined,
  OpenInNew,
  Bookmark,
  BugReport,
  CheckCircle,
  LockOutlined,
  WarningAmber,
  PersonOutlined,
  Assignment
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useJiraAccess } from '../hooks/useJiraAccess';
import { JiraProject, JiraSprint, JiraIssue } from '../types';
import {
  getMyJiraProjects,
  getActiveSprints,
  getSprintIssues,
  ATLASSIAN_SITE_URL,
  JiraApiError
} from '../services/jiraService';
import { JiraIssueDetailsDialog } from '../components/jira/JiraIssueDetailsDialog';

const JiraIntegrationPage: React.FC = () => {
  const { user, firebaseUser, openSignInModal, signOut } = useAuth();
  const { status: accessStatus, errorMessage: accessErrorMessage, retry: retryAccess } = useJiraAccess();

  // Tab: 'sprint' | 'my-work' | 'projects'
  const [activeTab, setActiveTab] = useState<'sprint' | 'my-work' | 'projects'>('sprint');

  // Projects state
  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [selectedProjectKey, setSelectedProjectKey] = useState<string>('');
  const [isLoadingProjects, setIsLoadingProjects] = useState<boolean>(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Active sprint state (supports zero, one, or multiple active sprints)
  const [activeSprints, setActiveSprints] = useState<JiraSprint[]>([]);
  const [selectedSprintId, setSelectedSprintId] = useState<string | number | null>(null);
  const [sprintIssues, setSprintIssues] = useState<JiraIssue[]>([]);
  const [isLoadingSprint, setIsLoadingSprint] = useState<boolean>(false);
  const [sprintError, setSprintError] = useState<string | null>(null);

  // Selected issue for detail modal
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);

  /**
   * Fetches active sprints and issues for the selected project.
   */
  const loadActiveSprintData = useCallback(async (projectKey: string) => {
    if (!projectKey) {
      setActiveSprints([]);
      setSelectedSprintId(null);
      setSprintIssues([]);
      return;
    }

    // Immediately clear previous sprint & issues to never show stale project data
    setActiveSprints([]);
    setSelectedSprintId(null);
    setSprintIssues([]);
    setIsLoadingSprint(true);
    setSprintError(null);

    try {
      const sprints = await getActiveSprints(projectKey);
      setActiveSprints(sprints);

      if (sprints.length > 0) {
        const chosen = sprints[0];
        const chosenId = chosen.id ?? (chosen as any).sprint_id;
        setSelectedSprintId(chosenId ?? null);

        if (chosenId) {
          const issues = await getSprintIssues(chosenId);
          setSprintIssues(Array.isArray(issues) ? issues : []);
        } else {
          setSprintIssues([]);
        }
      } else {
        setSprintIssues([]);
      }
    } catch (err: any) {
      console.warn(`Failed to load active sprint for ${projectKey}:`, err);
      const msg = err instanceof JiraApiError ? err.message : 'Unable to load active sprint.';
      setSprintError(msg);
      setActiveSprints([]);
      setSelectedSprintId(null);
      setSprintIssues([]);
    } finally {
      setIsLoadingSprint(false);
    }
  }, []);

  /**
   * Switches the active sprint when multiple active sprints are returned.
   */
  const handleSelectSprint = async (sprintId: string | number) => {
    setSelectedSprintId(sprintId);
    setIsLoadingSprint(true);
    setSprintError(null);
    try {
      const issues = await getSprintIssues(sprintId);
      setSprintIssues(Array.isArray(issues) ? issues : []);
    } catch (err: any) {
      console.warn(`Failed to load sprint issues for sprint ${sprintId}:`, err);
      setSprintError(err instanceof JiraApiError ? err.message : 'Failed to load sprint issues.');
    } finally {
      setIsLoadingSprint(false);
    }
  };

  /**
   * Fetches all visible Jira projects and initiates project workflow.
   */
  const loadProjects = useCallback(async () => {
    setIsLoadingProjects(true);
    setProjectsError(null);
    try {
      const fetched = await getMyJiraProjects();
      setProjects(fetched);
      const targetKey = selectedProjectKey || (fetched.length > 0 ? fetched[0].key : '');
      if (targetKey) {
        setSelectedProjectKey(targetKey);
        loadActiveSprintData(targetKey);
      }
    } catch (err: any) {
      console.warn('Failed to load Jira projects:', err);
      const msg = err instanceof JiraApiError ? err.message : 'Unable to load Jira projects.';
      setProjectsError(msg);
    } finally {
      setIsLoadingProjects(false);
    }
  }, [selectedProjectKey, loadActiveSprintData]);

  // Clear all Jira data on sign out or account switching
  useEffect(() => {
    if (!user && !firebaseUser) {
      setProjects([]);
      setSelectedProjectKey('');
      setActiveSprints([]);
      setSelectedSprintId(null);
      setSprintIssues([]);
    }
  }, [user, firebaseUser]);

  // When access is granted, load initial data
  useEffect(() => {
    if (accessStatus === 'granted') {
      loadProjects();
    }
  }, [accessStatus, loadProjects]);

  // When selected project changes, load its active sprint
  useEffect(() => {
    if (accessStatus === 'granted' && selectedProjectKey) {
      loadActiveSprintData(selectedProjectKey);
    }
  }, [accessStatus, selectedProjectKey, loadActiveSprintData]);

  // Currently viewed active sprint (from zero, one, or multiple active sprints)
  const currentSprint = useMemo(() => {
    if (!activeSprints.length) return null;
    if (selectedSprintId) {
      const match = activeSprints.find((s) => (s.id ?? (s as any).sprint_id) === selectedSprintId);
      if (match) return match;
    }
    return activeSprints[0];
  }, [activeSprints, selectedSprintId]);

  // Sprint metrics calculation
  const sprintMetrics = useMemo(() => {
    const total = sprintIssues.length;
    let done = 0;
    let inProgress = 0;
    let todo = 0;

    sprintIssues.forEach((issue) => {
      const s = (issue.status || '').toUpperCase();
      if (s.includes('DONE') || s.includes('RESOLVED') || s.includes('CLOSED')) {
        done++;
      } else if (s.includes('PROGRESS') || s.includes('REVIEW')) {
        inProgress++;
      } else {
        todo++;
      }
    });

    return { total, done, inProgress, todo };
  }, [sprintIssues]);

  // My Active Work: filtered directly from the active sprint state based on user email/name (zero extra API calls)
  const myIssues = useMemo(() => {
    if (!sprintIssues.length) return [];
    const userEmailLower = (user?.email || firebaseUser?.email || '').toLowerCase().trim();
    const userNameLower = (user?.displayName || firebaseUser?.displayName || '').toLowerCase().trim();

    return sprintIssues.filter((issue) => {
      const assigneeEmail = (issue.assignee?.emailAddress || '').toLowerCase().trim();
      const assigneeName = (issue.assignee?.displayName || '').toLowerCase().trim();

      if (userEmailLower && assigneeEmail && assigneeEmail === userEmailLower) return true;
      if (userEmailLower && assigneeName && (userEmailLower.includes(assigneeName) || assigneeName.includes(userEmailLower.split('@')[0]))) return true;
      if (userNameLower && assigneeName && (assigneeName === userNameLower || assigneeName.includes(userNameLower) || userNameLower.includes(assigneeName))) return true;
      return false;
    });
  }, [sprintIssues, user, firebaseUser]);

  const selectedProject = projects.find((p) => p.key === selectedProjectKey) || projects[0];

  const getIssueTypeIcon = (type?: string) => {
    const t = (type || '').toLowerCase();
    if (t.includes('bug')) return <BugReport sx={{ fontSize: 16, color: '#FF6577' }} />;
    if (t.includes('story')) return <Bookmark sx={{ fontSize: 16, color: '#1ca87f' }} />;
    if (t.includes('task')) return <Assignment sx={{ fontSize: 16, color: 'var(--hp-primary)' }} />;
    return <CheckCircle sx={{ fontSize: 16, color: 'var(--hp-primary)' }} />;
  };

  const getStatusColor = (status?: string) => {
    const s = (status || '').toUpperCase();
    if (s.includes('DONE') || s.includes('RESOLVED')) {
      return { bgcolor: 'rgba(34, 201, 154, 0.14)', color: '#1ca87f' };
    }
    if (s.includes('PROGRESS') || s.includes('REVIEW')) {
      return { bgcolor: 'rgba(79, 107, 255, 0.14)', color: 'var(--hp-primary)' };
    }
    return { bgcolor: 'var(--hp-surface-raised)', color: 'var(--hp-text-secondary)' };
  };

  // =========================================================================
  // STATE 1: UNAUTHENTICATED
  // =========================================================================
  if (accessStatus === 'unauthenticated') {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', gap: 2.5 }}>
        <Avatar sx={{ width: 64, height: 64, bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', mb: 1 }}>
          <LoginOutlined sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text)', textAlign: 'center' }}>
          Sign in to Access Jira Cloud Integration
        </Typography>
        <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ maxWidth: 460, textAlign: 'center', lineHeight: 1.6 }}>
          Connect with your Google account to view your ScrumAIApp Atlassian organization projects, active sprints, and stories.
        </Typography>
        <Button
          variant="contained"
          onClick={() => openSignInModal('workspace')}
          startIcon={<LoginOutlined />}
          sx={{
            bgcolor: 'var(--hp-primary)',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3,
            py: 1,
            '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
          }}
        >
          Sign In with Google
        </Button>
      </Box>
    );
  }

  // =========================================================================
  // STATE 2: CHECKING ACCESS
  // =========================================================================
  if (accessStatus === 'checking') {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 2 }}>
        <CircularProgress size={44} sx={{ color: 'var(--hp-primary)' }} />
        <Typography variant="body1" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
          Verifying Atlassian Organization Access...
        </Typography>
        <Typography variant="caption" color="var(--hp-text-secondary)">
          Validating authorization for {user?.email || 'your account'} against {ATLASSIAN_SITE_URL}
        </Typography>
      </Box>
    );
  }

  // =========================================================================
  // STATE 3: ACCESS DENIED (NOT AN ATLASSIAN ORG MEMBER)
  // =========================================================================
  if (accessStatus === 'denied') {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <Card className="neumorphic-card" sx={{ maxWidth: 540, p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: '20px' }}>
          <Avatar sx={{ width: 62, height: 62, bgcolor: 'rgba(255, 101, 119, 0.12)', color: '#FF6577', mx: 'auto', mb: 2 }}>
            <LockOutlined sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text)', mb: 1 }}>
            Atlassian Organization Access Required
          </Typography>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ lineHeight: 1.6, mb: 2 }}>
            Your signed-in account (<strong>{user?.email || firebaseUser?.email}</strong>) is not currently an active member of the ScrumAIApp Atlassian organization.
          </Typography>
          <Alert severity="warning" sx={{ mb: 3, textAlign: 'left', borderRadius: '10px', fontSize: '0.82rem' }}>
            To view Jira Cloud features, please ask your ScrumAIApp administrator or project lead to invite your email to{' '}
            <strong>scrumaiapp.atlassian.net</strong>.
          </Alert>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={retryAccess}
              startIcon={<Refresh />}
              sx={{
                bgcolor: 'var(--hp-primary)',
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5
              }}
            >
              Retry Access Check
            </Button>
            <Button
              variant="outlined"
              onClick={() => signOut()}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                color: 'var(--hp-text)'
              }}
            >
              Switch Account
            </Button>
          </Box>
        </Card>
      </Box>
    );
  }

  // =========================================================================
  // STATE 4: SERVICE ERROR / UNAVAILABLE
  // =========================================================================
  if (accessStatus === 'error') {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh', gap: 2 }}>
        <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255, 170, 0, 0.14)', color: '#FFAA00' }}>
          <WarningAmber sx={{ fontSize: 32 }} />
        </Avatar>
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
          Jira Cloud Service Unavailable
        </Typography>
        <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ maxWidth: 480, textAlign: 'center', lineHeight: 1.6 }}>
          {accessErrorMessage || 'Unable to establish connection with the ScrumAI Jira Cloud API. Please check your network and retry.'}
        </Typography>
        <Button
          variant="contained"
          onClick={retryAccess}
          startIcon={<Refresh />}
          sx={{
            mt: 1,
            bgcolor: 'var(--hp-primary)',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 700,
            px: 3
          }}
        >
          Retry Connection
        </Button>
      </Box>
    );
  }

  // =========================================================================
  // STATE 5: ACCESS GRANTED -> RENDER JIRA DASHBOARD
  // =========================================================================
  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, height: '100%', overflowY: 'auto', bgcolor: 'var(--hp-bg)' }}>
      {/* Dashboard Top Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          gap: 2,
          mb: 3
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
              Jira Cloud Dashboard
            </Typography>
            <Chip
              label="Atlassian Org Member Verified"
              size="small"
              sx={{
                bgcolor: 'rgba(34, 201, 154, 0.14)',
                color: '#1ca87f',
                fontWeight: 700,
                fontSize: '0.72rem'
              }}
            />
          </Box>
          <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5 }}>
            Connected to <strong>{ATLASSIAN_SITE_URL.replace('https://', '')}</strong> via Cloud Run REST API
          </Typography>
        </Box>

        {/* Global Action Controls */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            onClick={() => {
              loadProjects();
              if (selectedProjectKey) {
                loadActiveSprintData(selectedProjectKey);
              }
            }}
            disabled={isLoadingProjects || isLoadingSprint}
            startIcon={isLoadingProjects || isLoadingSprint ? <CircularProgress size={16} /> : <Refresh />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.85rem',
              px: 2
            }}
          >
            Refresh Jira
          </Button>

          <Button
            variant="contained"
            component="a"
            href={ATLASSIAN_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNew />}
            sx={{
              bgcolor: 'var(--hp-primary)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.85rem',
              px: 2.2,
              '&:hover': { bgcolor: 'var(--hp-primary-dark)' }
            }}
          >
            Open Jira Site
          </Button>
        </Box>
      </Box>

      {/* Project Selector Toolbar */}
      <Box
        className="neumorphic-card"
        sx={{
          p: 1.8,
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: 'var(--hp-primary)',
              color: '#fff',
              fontWeight: 800,
              width: 38,
              height: 38
            }}
          >
            {selectedProject?.key || 'JIRA'}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'var(--hp-text)' }}>
              {selectedProject?.name || 'Select a Jira Project'}
            </Typography>
            <Typography variant="caption" color="var(--hp-text-secondary)">
              Key: {selectedProject?.key || 'None'} • {projects.length} accessible project(s)
            </Typography>
          </Box>
        </Box>

        {/* Project Selector Dropdown */}
        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 240 } }}>
          <InputLabel id="jira-project-select-label">Active Jira Project</InputLabel>
          <Select
            labelId="jira-project-select-label"
            value={selectedProjectKey}
            label="Active Jira Project"
            onChange={(e) => setSelectedProjectKey(e.target.value)}
            disabled={isLoadingProjects || projects.length === 0}
            sx={{ borderRadius: '10px', fontSize: '0.88rem' }}
          >
            {projects.map((proj) => (
              <MenuItem key={proj.key} value={proj.key}>
                <strong>{proj.key}</strong> - {proj.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Projects Error Notice if any */}
      {projectsError && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: '12px' }}>
          {projectsError}
        </Alert>
      )}

      {/* Navigation Tabs: Active Sprint, My Work, Projects */}
      <Box sx={{ borderBottom: 1, borderColor: 'var(--hp-border)', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.9rem',
              minWidth: 130
            }
          }}
        >
          <Tab value="sprint" label="Active Sprint" icon={<Schedule sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab value="my-work" label={`My Active Work (${myIssues.length})`} icon={<PersonOutlined sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab value="projects" label={`All Projects (${projects.length})`} icon={<Folder sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* ======================================================== */}
      {/* TAB 1: ACTIVE SPRINT EXPLORER */}
      {/* ======================================================== */}
      {activeTab === 'sprint' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {isLoadingSprint ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1.5 }}>
              <CircularProgress size={36} sx={{ color: 'var(--hp-primary)' }} />
              <Typography variant="body2" color="var(--hp-text-secondary)">
                Loading active sprint for {selectedProjectKey}...
              </Typography>
            </Box>
          ) : sprintError ? (
            <Alert
              severity="warning"
              action={
                <Button size="small" color="inherit" onClick={() => loadActiveSprintData(selectedProjectKey)}>
                  Retry
                </Button>
              }
              sx={{ borderRadius: '12px' }}
            >
              {sprintError}
            </Alert>
          ) : !currentSprint ? (
            <Box className="neumorphic-card" sx={{ p: 5, textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 48, color: 'var(--hp-text-secondary)', opacity: 0.4, mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
                No Active Sprint Running
              </Typography>
              <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ maxWidth: 500, mx: 'auto', mt: 0.5, lineHeight: 1.6 }}>
                Project <strong>{selectedProjectKey}</strong> does not currently have an active sprint. Start a sprint in Jira to explore real-time tasks.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Sprint Header Banner */}
              <Box className="neumorphic-card" sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Multi-Sprint Selector if more than 1 active sprint */}
                {activeSprints.length > 1 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1, borderBottom: '1px solid var(--hp-border)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--hp-text-secondary)' }}>
                      Select Active Sprint ({activeSprints.length}):
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <Select
                        value={selectedSprintId || (currentSprint.id ?? (currentSprint as any).sprint_id)}
                        onChange={(e) => handleSelectSprint(e.target.value)}
                        sx={{ borderRadius: '8px', fontSize: '0.82rem', height: 32 }}
                      >
                        {activeSprints.map((sp) => {
                          const spId = sp.id ?? (sp as any).sprint_id;
                          return (
                            <MenuItem key={String(spId)} value={spId}>
                              {sp.name} {sp.state ? `(${sp.state})` : ''}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Schedule sx={{ color: 'var(--hp-primary)', fontSize: 24 }} />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
                      {currentSprint.name}
                    </Typography>
                  </Box>
                  <Chip
                    label={currentSprint.state || 'ACTIVE'}
                    size="small"
                    sx={{
                      fontWeight: 700,
                      bgcolor: 'rgba(34, 201, 154, 0.14)',
                      color: '#1ca87f',
                      border: '1px solid rgba(34, 201, 154, 0.3)'
                    }}
                  />
                </Box>

                {currentSprint.goal && (
                  <Typography variant="body2" sx={{ bgcolor: 'var(--hp-bg)', p: 1.2, borderRadius: '8px', color: 'var(--hp-text)' }}>
                    <strong>Sprint Goal:</strong> {currentSprint.goal}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', color: 'var(--hp-text-secondary)', fontSize: '0.8rem' }}>
                  {currentSprint.startDate && (
                    <Typography variant="caption">
                      Started: {new Date(currentSprint.startDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {currentSprint.endDate && (
                    <Typography variant="caption">
                      Ends: {new Date(currentSprint.endDate).toLocaleDateString()}
                    </Typography>
                  )}
                  {currentSprint.boardId && (
                    <Typography variant="caption">
                      Board ID: {currentSprint.boardId}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Sprint Summary Metrics */}
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box className="neumorphic-card" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
                      {sprintMetrics.total}
                    </Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">
                      Total Sprint Issues
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box className="neumorphic-card" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#1ca87f' }}>
                      {sprintMetrics.done}
                    </Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">
                      Completed (Done)
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box className="neumorphic-card" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-primary)' }}>
                      {sprintMetrics.inProgress}
                    </Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">
                      In Progress
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box className="neumorphic-card" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'var(--hp-text-secondary)' }}>
                      {sprintMetrics.todo}
                    </Typography>
                    <Typography variant="caption" color="var(--hp-text-secondary)">
                      To Do
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Sprint Issues Table */}
              <TableContainer component={Paper} className="neumorphic-card" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'var(--hp-surface-raised)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Key</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Summary</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Priority</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Assignee</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sprintIssues.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'var(--hp-text-secondary)' }}>
                          No issues currently associated with this sprint.
                        </TableCell>
                      </TableRow>
                    ) : (
                      sprintIssues.map((issue) => {
                        const statusStyle = getStatusColor(issue.status);
                        return (
                          <TableRow
                            key={issue.key}
                            hover
                            onClick={() => setSelectedIssueKey(issue.key)}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'var(--hp-surface-raised)' } }}
                          >
                            <TableCell>{getIssueTypeIcon(issue.issueType)}</TableCell>
                            <TableCell>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--hp-primary)' }}>
                                {issue.key}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320 }}>
                              <Typography
                                sx={{
                                  fontSize: '0.84rem',
                                  fontWeight: 600,
                                  color: 'var(--hp-text)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                              >
                                {issue.summary}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={issue.status || 'To Do'}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.68rem',
                                  fontWeight: 700,
                                  ...statusStyle
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {issue.priority || 'Medium'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                <Avatar sx={{ width: 20, height: 20, fontSize: '0.62rem', bgcolor: 'var(--hp-primary)' }}>
                                  {(issue.assignee?.displayName || 'U').charAt(0).toUpperCase()}
                                </Avatar>
                                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                  {issue.assignee?.displayName || 'Unassigned'}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 2: MY ACTIVE WORK (Filtered from active sprint state) */}
      {/* ======================================================== */}
      {activeTab === 'my-work' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
                My Assigned Active Sprint Issues ({myIssues.length})
              </Typography>
              <Typography variant="caption" color="var(--hp-text-secondary)">
                Showing sprint issues assigned to <strong>{user?.email || firebaseUser?.email}</strong> in project {selectedProjectKey}
              </Typography>
            </Box>
          </Box>

          {myIssues.length === 0 ? (
            <Box className="neumorphic-card" sx={{ p: 5, textAlign: 'center' }}>
              <PersonOutlined sx={{ fontSize: 44, opacity: 0.4, color: 'var(--hp-text-secondary)', mb: 1.5 }} />
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'var(--hp-text)' }}>
                No active issues assigned to you
              </Typography>
              <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5 }}>
                There are no active sprint tasks currently assigned to <strong>{user?.email || firebaseUser?.email}</strong> in project {selectedProjectKey}.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} className="neumorphic-card" sx={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'var(--hp-surface-raised)' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Summary</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {myIssues.map((issue) => {
                    const statusStyle = getStatusColor(issue.status);
                    return (
                      <TableRow
                        key={issue.key}
                        hover
                        onClick={() => setSelectedIssueKey(issue.key)}
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'var(--hp-surface-raised)' } }}
                      >
                        <TableCell>{getIssueTypeIcon(issue.issueType)}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--hp-primary)' }}>
                            {issue.key}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 360 }}>
                          <Typography
                            sx={{
                              fontSize: '0.84rem',
                              fontWeight: 600,
                              color: 'var(--hp-text)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {issue.summary}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={issue.status || 'To Do'}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              ...statusStyle
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {issue.priority || 'Medium'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            startIcon={<OpenInNew sx={{ fontSize: 13 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedIssueKey(issue.key);
                            }}
                            sx={{ textTransform: 'none', fontSize: '0.74rem', py: 0.2 }}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ======================================================== */}
      {/* TAB 3: ALL PROJECTS */}
      {/* ======================================================== */}
      {activeTab === 'projects' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: 'var(--hp-text)' }}>
            Accessible Jira Cloud Projects ({projects.length})
          </Typography>

          {isLoadingProjects ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={36} sx={{ color: 'var(--hp-primary)' }} />
            </Box>
          ) : projects.length === 0 ? (
            <Box className="neumorphic-card" sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>No Jira projects visible</Typography>
              <Typography variant="body2" color="var(--hp-text-secondary)" sx={{ mt: 0.5 }}>
                Your account does not have access to any Jira projects in this organization.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2.5}>
              {projects.map((proj) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={proj.key}>
                  <Box
                    className="neumorphic-card"
                    sx={{
                      p: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      cursor: 'pointer',
                      border: selectedProjectKey === proj.key ? '1px solid var(--hp-primary)' : '1px solid transparent',
                      transition: 'all 0.18s ease',
                      '&:hover': { bgcolor: 'var(--hp-surface-raised)', borderColor: 'var(--hp-primary)' }
                    }}
                    onClick={() => {
                      setSelectedProjectKey(proj.key);
                      setActiveTab('sprint');
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ bgcolor: 'var(--hp-primary-light)', color: 'var(--hp-primary)', fontWeight: 800 }}>
                        {proj.key.slice(0, 3)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: 'var(--hp-text)' }}>
                          {proj.name}
                        </Typography>
                        <Chip size="small" label={proj.key} sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }} />
                      </Box>
                    </Box>

                    {proj.description && (
                      <Typography
                        variant="body2"
                        color="var(--hp-text-secondary)"
                        sx={{
                          fontSize: '0.82rem',
                          maxHeight: 50,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {proj.description}
                      </Typography>
                    )}

                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        mt: 'auto',
                        bgcolor: 'var(--hp-primary)',
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 600
                      }}
                    >
                      View Active Sprint →
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Details Dialog */}
      <JiraIssueDetailsDialog
        open={Boolean(selectedIssueKey)}
        issueKey={selectedIssueKey}
        onClose={() => setSelectedIssueKey(null)}
        onSelectIssue={(key) => setSelectedIssueKey(key)}
      />
    </Box>
  );
};

export default JiraIntegrationPage;
